import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Metric } from "../Metric";
import { fmt } from "../../lib/api";

const avg = (arr, k) => (arr.length ? arr.reduce((s, t) => s + t[k], 0) / arr.length : null);
const TYPES = [["upload", "Upload"], ["download", "Download"], ["ping", "Lag (ping)"], ["jitter", "Jitter"]];

const pctColor = (p) => (p > 20 ? "text-[#FF3B30]" : "text-[#32D74B]");

const localDay = (iso, offset = 0) => {
  const d = new Date(iso ? new Date(iso) : Date.now() - offset * 864e5);
  return new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 10);
};
const hasIssue = (t, k) => t.issues.some((i) => i.toLowerCase().includes(k));
const dayRate = (ts, k) => (ts.length ? (ts.filter((t) => (k ? hasIssue(t, k) : t.issues.length)).length / ts.length) * 100 : null);

const Delta = ({ today, yest, testId }) => {
  if (today == null || yest == null) return <div className="text-[10px] text-neutral-600" data-testid={testId}>{today == null ? "No scans today" : "No scans yesterday"}</div>;
  const d = Math.round(today - yest);
  const cls = d > 0 ? "text-[#FF3B30]" : d < 0 ? "text-[#32D74B]" : "text-neutral-500";
  return (
    <div className={`text-[10px] flex items-center gap-1 whitespace-nowrap ${cls}`} data-testid={testId} title={`Today ${Math.round(today)}% · Yesterday ${Math.round(yest)}%`}>
      {d > 0 ? <TrendingUp className="w-3 h-3" /> : d < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
      {d > 0 ? "+" : ""}{d} pts <span className="text-neutral-500">vs yesterday</span>
    </div>
  );
};

const IssueSplit = ({ tests, undated }) => {
  const todayTs = undated.filter((t) => localDay(t.created_at) === localDay(null, 0));
  const yestTs = undated.filter((t) => localDay(t.created_at) === localDay(null, 1));
  const total = tests.filter((t) => t.issues.length).length;
  const rate = (n) => (tests.length ? (n / tests.length) * 100 : 0);
  return (
    <div className="col-span-2 md:col-span-4 p-4 border-r border-b border-white/10" data-testid="kpi-issues">
      <div className="label">With issues <span className="text-white ml-1" data-testid="kpi-issues-total">{total}</span>
        <span className={`ml-2 ${pctColor(rate(total))}`} data-testid="kpi-issues-total-pct">{Math.round(rate(total))}% of scans</span></div>
      <Delta testId="kpi-issues-total-delta" today={dayRate(todayTs)} yest={dayRate(yestTs)} />
      <div className="grid grid-cols-4 mt-1 divide-x divide-white/10">
        {TYPES.map(([k, l]) => {
          const n = tests.filter((t) => t.issues.some((i) => i.toLowerCase().includes(k))).length;
          const p = rate(n);
          return (
            <div key={k} className="px-3 first:pl-0" data-testid={`kpi-issues-${k}`}>
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{l}</div>
              <div className="flex items-baseline gap-2">
                <span className={`font-display text-4xl ${pctColor(p)}`} data-testid={`kpi-issues-${k}-pct`}>{Math.round(p)}%</span>
                <span className="text-xs text-neutral-400" data-testid={`kpi-issues-${k}-count`}>{n} issue{n === 1 ? "" : "s"}</span>
              </div>
              <Delta testId={`kpi-issues-${k}-delta`} today={dayRate(todayTs, k)} yest={dayRate(yestTs, k)} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Kpis = ({ tests, undated = tests }) => {
  const reps = new Set(tests.map((t) => t.rep_name)).size;
  return (
    <div className="grid grid-cols-2 md:grid-cols-9 border-b border-l border-white/10 fade-up" data-testid="kpi-row">
      <Metric testId="kpi-total-tests" label="Total tests" value={tests.length} />
      <IssueSplit tests={tests} undated={undated} />
      <Metric testId="kpi-reps" label="Active reps" value={reps} />
      <Metric testId="kpi-avg-download" label="Avg download" unit="Mbps" value={fmt(avg(tests, "download_mbps"))} />
      <Metric testId="kpi-avg-upload" label="Avg upload" unit="Mbps" value={fmt(avg(tests, "upload_mbps"))} />
      <Metric testId="kpi-avg-ping" label="Avg ping" unit="ms" value={fmt(avg(tests, "ping_ms"), 0)} />
    </div>
  );
};
